import React, { useState, useMemo } from 'react';
import { toWhatsAppPhone } from '../../utils/whatsappPhone';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { propertyService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Property } from '../../types';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import * as Icons from 'react-icons/fa';
import { transactionPhrase } from '../../utils/transactionType';
const { FaWhatsapp, FaSort, FaSortUp, FaSortDown } = Icons as any;

// Styled Components (baseados em AdminProperties.tsx)
const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.875rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ClearButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const PropertiesTable = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow-x: auto;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.background};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;

  th {
    padding: ${({ theme }) => theme.spacing.md};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
  }

  .sort-icon {
    margin-left: ${({ theme }) => theme.spacing.xs};
    opacity: 0.5;
    font-size: 0.75rem;
  }

  &.active .sort-icon {
    opacity: 1;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }

  td {
    padding: ${({ theme }) => theme.spacing.md};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text.primary};
    vertical-align: middle;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
`;

const ActionButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  
  &.whatsapp {
    background-color: #25D366;
    color: white;
    &:hover {
      background-color: #1EAE54;
    }
  }

  &.status {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.light};
    &:hover {
      background-color: ${({ theme }) => theme.colors.secondary};
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusModal = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StatusTitle = styled.h3`
  font-size: 1.125rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatusOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }

  input[type="radio"] {
    cursor: pointer;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;

  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background-color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.text.light : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ITEMS_PER_PAGE = 20;

type SortField = 'title' | 'created_at' | 'last_contact' | 'owner__name';
type SortOrder = 'asc' | 'desc';

const PropertyFollowUp: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('disponivel');
  const [markAsVerified, setMarkAsVerified] = useState(false);
  
  // @AI_COMMENTS: Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    hasPhone: '',
    daysWithoutContact: ''
  });
  
  // @AI_COMMENTS: Estados para ordenação - padrão: último contato (nulls first)
  const [sortField, setSortField] = useState<SortField>('last_contact');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const { data: response, isLoading, error } = useQuery(
    ['propertiesForFollowUp', currentPage, filters, sortField, sortOrder],
    () => propertyService.getForFollowUp({
      skip: (currentPage - 1) * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
      search: filters.search,
      status: filters.status,
      type: filters.type,
      has_phone: filters.hasPhone,
      days_without_contact: filters.daysWithoutContact,
      sort_by: sortField,
      sort_order: sortOrder,
    }),
    {
      keepPreviousData: true,
    }
  );

  const properties = useMemo(() => response?.items || [], [response]);
  const totalItems = useMemo(() => response?.total || 0, [response]);
  const totalPages = useMemo(() => response?.pages || 0, [response]);

  // @AI_COMMENTS: Função removida - agora abre WhatsApp Web diretamente

  // @AI_COMMENTS: Mutation para atualizar status do imóvel
  const updateStatusMutation = useMutation(
    ({ propertyId, status, verified }: { propertyId: string; status: string; verified: boolean }) =>
      propertyService.updatePropertyStatus(propertyId, status, verified),
    {
      onSuccess: () => {
        showNotification('Status do imóvel atualizado com sucesso!', 'success');
        // @AI_COMMENTS: Pequeno delay para garantir que o backend processou
        setTimeout(() => {
          queryClient.invalidateQueries(['propertiesForFollowUp']);
        }, 500);
        setStatusModalOpen(false);
        setSelectedProperty(null);
        setSelectedStatus('');
        setMarkAsVerified(false);
      },
      onError: () => {
        showNotification('Erro ao atualizar status do imóvel', 'error');
      },
    }
  );

  const handleVerify = (property: Property) => {
    if (!property.owner?.phone) {
      showNotification('Imóvel sem telefone cadastrado', 'error');
      return;
    }
    
    // @AI_COMMENTS: Formatar telefone removendo caracteres especiais
    const phone = property.owner.phone.replace(/\D/g, '');
    
    // @AI_COMMENTS: Criar mensagem de verificação
    const transactionType = transactionPhrase(property.transaction_type);
    
    // @AI_COMMENTS: Extrair dados de localização do campo address
    const address = property.address || {};
    const neighborhood = address.neighborhood || 'Não informado';
    const city = address.city || 'Não informado';
    const state = address.state || 'Não informado';
    
    const message = `Olá ${property.owner.name}!

Somos da imobiliária e gostaríamos de verificar se o imóvel abaixo ainda está disponível para ${transactionType}:

📍 *${property.title}*
📐 Área: ${property.area}m²
🏠 ${property.bedrooms} quartos, ${property.bathrooms} banheiros
💰 Valor: R$ ${property.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
📌 Localização: ${neighborhood}, ${city}/${state}

Por favor, responda se o imóvel ainda está disponível.

Obrigado!`;
    
    // @AI_COMMENTS: Codificar mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // @AI_COMMENTS: Abrir WhatsApp Web em nova aba
    const whatsappUrl = `https://wa.me/${toWhatsAppPhone(phone) || phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    // @AI_COMMENTS: Registrar tentativa de contato via API
    propertyService.sendVerificationMessage(property.id)
      .then(() => {
        showNotification('Link do WhatsApp aberto! Registrando contato...', 'success');
        // @AI_COMMENTS: Pequeno delay para garantir que o backend processou
        setTimeout(() => {
          queryClient.invalidateQueries(['propertiesForFollowUp']);
        }, 500);
      })
      .catch(() => {
        // @AI_COMMENTS: Não mostrar erro pois o WhatsApp já foi aberto
        console.log('Erro ao registrar contato, mas WhatsApp foi aberto');
      });
  };

  const handleChangeStatus = (property: Property) => {
    const normalizeStatus = (status?: string) => {
      if (!status) return 'disponivel';
      return status
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // remove acentos
    };

    setSelectedProperty(property);
    setSelectedStatus(normalizeStatus(property.status));
    setStatusModalOpen(true);
  };

  const handleSaveStatus = () => {
    if (!selectedProperty || !selectedStatus) return;
    
    updateStatusMutation.mutate({
      propertyId: selectedProperty.id,
      status: selectedStatus,
      verified: markAsVerified,
    });
  };
  
  // @AI_COMMENTS: Função para lidar com ordenação
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };
  
  // @AI_COMMENTS: Função para renderizar ícone de ordenação
  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return <FaSort className="sort-icon" />;
    return sortOrder === 'asc' ? 
      <FaSortUp className="sort-icon" /> : 
      <FaSortDown className="sort-icon" />;
  };
  
  // @AI_COMMENTS: Função para limpar filtros
  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      type: '',
      hasPhone: '',
      daysWithoutContact: ''
    });
    setCurrentPage(1);
  };
  
  if (error) {
    return <Container>Ocorreu um erro ao buscar os imóveis.</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Acompanhamento de Imóveis Disponíveis</Title>
      </Header>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel>Buscar</FilterLabel>
          <FilterInput
            type="text"
            placeholder="Título, proprietário, telefone..."
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setCurrentPage(1);
            }}
          />
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect
            value={filters.status}
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="disponivel">Disponível</option>
            <option value="vendido">Vendido</option>
            <option value="alugado">Alugado</option>
            <option value="cancelado">Cancelado</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Tipo</FilterLabel>
          <FilterSelect
            value={filters.type}
            onChange={(e) => {
              setFilters({ ...filters, type: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="casa">Casa</option>
            <option value="apartamento">Apartamento</option>
            <option value="terreno">Terreno</option>
            <option value="comercial">Comercial</option>
            <option value="rural">Rural</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Telefone</FilterLabel>
          <FilterSelect
            value={filters.hasPhone}
            onChange={(e) => {
              setFilters({ ...filters, hasPhone: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="true">Com telefone</option>
            <option value="false">Sem telefone</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Dias sem contato</FilterLabel>
          <FilterSelect
            value={filters.daysWithoutContact}
            onChange={(e) => {
              setFilters({ ...filters, daysWithoutContact: e.target.value });
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="7">7+ dias</option>
            <option value="15">15+ dias</option>
            <option value="30">30+ dias</option>
            <option value="60">60+ dias</option>
          </FilterSelect>
        </FilterGroup>
        
        {Object.values(filters).some(f => f !== '') && (
          <FilterGroup style={{ alignSelf: 'flex-end' }}>
            <ClearButton onClick={handleClearFilters}>
              Limpar Filtros
            </ClearButton>
          </FilterGroup>
        )}
      </FiltersContainer>

      <ResultsInfo>
        <span>
          Mostrando{' '}
          <strong>
            {isLoading ? '...' : Math.min(ITEMS_PER_PAGE * currentPage, totalItems)}
          </strong>{' '}
          de <strong>{isLoading ? '...' : totalItems}</strong> imóveis.
        </span>
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </ResultsInfo>

      <PropertiesTable>
        <Table>
          <TableHeader>
            <tr>
              <SortableHeader 
                onClick={() => handleSort('title')}
                className={sortField === 'title' ? 'active' : ''}
              >
                Título do Imóvel {getSortIcon('title')}
              </SortableHeader>
              <SortableHeader 
                onClick={() => handleSort('owner__name')}
                className={sortField === 'owner__name' ? 'active' : ''}
              >
                Proprietário {getSortIcon('owner__name')}
              </SortableHeader>
              <th>Telefone</th>
              <SortableHeader 
                onClick={() => handleSort('created_at')}
                className={sortField === 'created_at' ? 'active' : ''}
              >
                Data de Cadastro {getSortIcon('created_at')}
              </SortableHeader>
              <SortableHeader 
                onClick={() => handleSort('last_contact')}
                className={sortField === 'last_contact' ? 'active' : ''}
              >
                Último Contato {getSortIcon('last_contact')}
              </SortableHeader>
              <th>Ações</th>
            </tr>
          </TableHeader>
          <tbody>
            {isLoading ? (
              <TableRow>
                <td colSpan={6}>Carregando imóveis...</td>
              </TableRow>
            ) : properties && properties.length === 0 ? (
              <TableRow>
                <td colSpan={6}>Nenhum imóvel disponível para acompanhamento.</td>
              </TableRow>
            ) : properties ? (
              properties.map((property: Property) => (
                <TableRow key={property.id}>
                  <td>{property.title}</td>
                  <td>{property.owner?.name || 'Não informado'}</td>
                  <td>{property.owner?.phone || 'Não informado'}</td>
                  <td>{new Date(property.created_at).toLocaleDateString()}</td>
                  <td>
                    {property.last_contact 
                      ? new Date(property.last_contact).toLocaleDateString() 
                      : 'Nunca contatado'}
                  </td>
                  <td>
                    <ActionButtons>
                      <ActionButton
                        className="whatsapp"
                        onClick={() => handleVerify(property)}
                        disabled={!property.owner?.phone}
                      >
                        <FaWhatsapp />
                        Verificar
                      </ActionButton>
                      <ActionButton
                        className="status"
                        onClick={() => handleChangeStatus(property)}
                      >
                        Alterar Status
                      </ActionButton>
                    </ActionButtons>
                  </td>
                </TableRow>
              ))
            ) : null}
          </tbody>
        </Table>
      </PropertiesTable>
      
      {totalPages > 1 && (
         <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
      )}

      {statusModalOpen && selectedProperty && (
        <Modal
          title={`Alterar Status - ${selectedProperty.title}`}
          isOpen={statusModalOpen}
          onClose={() => {
            setStatusModalOpen(false);
            setSelectedProperty(null);
            setSelectedStatus('');
            setMarkAsVerified(false);
          }}
        >
          <StatusModal>
            <StatusTitle>Alterar Status - {selectedProperty.title}</StatusTitle>
            
            <StatusOptions>
              <StatusOption>
                <input
                  type="radio"
                  id="disponivel"
                  name="status"
                  value="disponivel"
                  checked={selectedStatus === 'disponivel'}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                />
                <label htmlFor="disponivel">Disponível</label>
              </StatusOption>
              
              <StatusOption>
                <input
                  type="radio"
                  id="vendido"
                  name="status"
                  value="vendido"
                  checked={selectedStatus === 'vendido'}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                />
                <label htmlFor="vendido">Vendido</label>
              </StatusOption>
              
              <StatusOption>
                <input
                  type="radio"
                  id="alugado"
                  name="status"
                  value="alugado"
                  checked={selectedStatus === 'alugado'}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                />
                <label htmlFor="alugado">Alugado</label>
              </StatusOption>
              
              <StatusOption>
                <input
                  type="radio"
                  id="cancelado"
                  name="status"
                  value="cancelado"
                  checked={selectedStatus === 'cancelado'}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                />
                <label htmlFor="cancelado">Cancelado</label>
              </StatusOption>
            </StatusOptions>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={markAsVerified}
                onChange={(e) => setMarkAsVerified(e.target.checked)}
              />
              Marcar como verificado (atualiza data de última verificação)
            </CheckboxLabel>

            <ModalActions>
              <ModalButton
                onClick={() => {
                  setStatusModalOpen(false);
                  setSelectedProperty(null);
                  setSelectedStatus('');
                  setMarkAsVerified(false);
                }}
              >
                Cancelar
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={handleSaveStatus}
                disabled={updateStatusMutation.isLoading}
              >
                {updateStatusMutation.isLoading ? 'Salvando...' : 'Salvar'}
              </ModalButton>
            </ModalActions>
          </StatusModal>
        </Modal>
      )}
    </Container>
  );
};

export default PropertyFollowUp; 
